import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { toast } from 'react-toastify';
import { format, isValid } from 'date-fns';
import { useHistory } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Story, StoryStatus } from '@graphql/types';
import { ConfirmationModal } from '@components/UI/ConfirmationModal/ConfirmationModal';
import { useUpdateStoryMutation } from '@components/Stories/StoryEditor/gql/StoryEditor.generated';
import { useStore } from '@stores/rootStoreContext';
import StoriesItem from '../StoriesItem/StoriesItem';
import { useGetStoriesQuery } from '../gql/stories.generated';
import { UiStories } from '../gql/stories.types';
import storiesAdapterServerToClient from '../gql/stories.adapters';
import { StoryAction } from '../../../../types/story/StoryAction';
import { StoriesListContentProps } from './StoriesListContentProps';
import { STORIES_LIST_CONTENT } from './StoriesListContent.consts';

const StoriesListContent: FC<StoriesListContentProps> = ({ status }) => {
    const { storiesListStore } = useStore();
    const { filters } = storiesListStore;
    const [isEmptyList, setIsEmptyList] = useState(false);
    const history = useHistory();
    const isFetchingRef = useRef(false);
    const lastRefetchRef = useRef<number>(0);
    const lastFiltersRef = useRef({
        searchString: filters.searchString,
        createdAtFrom: filters.createdAtFrom?.toISOString(),
        createdAtTo: filters.createdAtTo?.toISOString(),
    });

    const variables = {
        after: null,
        dateUpdatedFrom:
            filters.createdAtFrom && isValid(filters.createdAtFrom)
                ? format(filters.createdAtFrom, 'yyyy-MM-dd')
                : undefined,
        dateUpdatedTo:
            filters.createdAtTo && isValid(filters.createdAtTo)
                ? format(filters.createdAtTo, 'yyyy-MM-dd')
                : undefined,
        status: status || undefined,
        search: filters.searchString || undefined,
    };

    const { data, fetchMore, refetch, loading } = useGetStoriesQuery({
        skip: !status,
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-and-network',
        variables,
        notifyOnNetworkStatusChange: true,
        onCompleted: (data) => {
            if (data.stories) {
                const adaptedData = storiesAdapterServerToClient(data);
                console.log('onCompleted adaptedData:', adaptedData);
                setIsEmptyList(!adaptedData.edges.length);
            }
        },
        onError: (error) => {
            console.error('useGetStoriesQuery error:', error);
        },
    });

    console.log('useGetStoriesQuery called with variables:', variables);

    const adaptedData = data?.stories ? storiesAdapterServerToClient(data) : undefined;

    useEffect(() => {
        if (!status) return;

        const newVariables = {
            after: null,
            status: status || undefined,
            search: filters.searchString || undefined,
            dateUpdatedFrom:
                filters.createdAtFrom && isValid(filters.createdAtFrom)
                    ? format(filters.createdAtFrom, 'yyyy-MM-dd')
                    : undefined,
            dateUpdatedTo:
                filters.createdAtTo && isValid(filters.createdAtTo)
                    ? format(filters.createdAtTo, 'yyyy-MM-dd')
                    : undefined,
        };

        const filtersChanged =
            filters.searchString !== lastFiltersRef.current.searchString ||
            filters.createdAtFrom?.toISOString() !== lastFiltersRef.current.createdAtFrom ||
            filters.createdAtTo?.toISOString() !== lastFiltersRef.current.createdAtTo;

        const timeSinceLastRefetch = Date.now() - lastRefetchRef.current > 1000;

        if (filtersChanged && timeSinceLastRefetch) {
            lastRefetchRef.current = Date.now();
            lastFiltersRef.current = {
                searchString: filters.searchString,
                createdAtFrom: filters.createdAtFrom?.toISOString(),
                createdAtTo: filters.createdAtTo?.toISOString(),
            };
            console.log('refetch called with variables:', newVariables);
            refetch(newVariables);
        }
    }, [filters.searchString, filters.createdAtFrom, filters.createdAtTo, status, refetch]);

    const fetchMoreData = useCallback(() => {
        if (isFetchingRef.current || !data?.stories?.pageInfo.hasNextPage || loading) {
            console.log('fetchMoreData skipped:', {
                isFetching: isFetchingRef.current,
                hasNextPage: data?.stories?.pageInfo.hasNextPage,
                loading,
            });
            return;
        }

        isFetchingRef.current = true;
        console.log('fetchMoreData called with cursor:', data?.stories?.pageInfo.endCursor);

        fetchMore({
            variables: {
                first: 20,
                after: data?.stories?.pageInfo.endCursor,
                status: status || undefined,
                search: filters.searchString || undefined,
                dateUpdatedFrom:
                    filters.createdAtFrom && isValid(filters.createdAtFrom)
                        ? format(filters.createdAtFrom, 'yyyy-MM-dd')
                        : undefined,
                dateUpdatedTo:
                    filters.createdAtTo && isValid(filters.createdAtTo)
                        ? format(filters.createdAtTo, 'yyyy-MM-dd')
                        : undefined,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                isFetchingRef.current = false;
                if (!fetchMoreResult?.stories?.edges) {
                    console.log('fetchMoreResult empty or invalid:', fetchMoreResult);
                    return prev;
                }

                console.log('fetchMoreResult.stories.edges:', fetchMoreResult.stories.edges);
                const newData = storiesAdapterServerToClient(fetchMoreResult);
                console.log('fetchMore newData:', newData);

                return {
                    stories: {
                        ...fetchMoreResult.stories,
                        edges: [...(prev.stories?.edges || []), ...fetchMoreResult.stories.edges],
                        pageInfo: fetchMoreResult.stories.pageInfo,
                    },
                };
            },
        }).catch((error) => {
            isFetchingRef.current = false;
            console.error('fetchMore error:', error);
        });
    }, [fetchMore, filters.createdAtFrom, filters.createdAtTo, filters.searchString, status, data?.stories?.pageInfo.hasNextPage, loading, data?.stories?.pageInfo.endCursor]);

    const [updateStory] = useUpdateStoryMutation();

    const updateStoryAction = useCallback(
        async (uid: string, status: StoryStatus) => {
            try {
                const updateStoryResult = await updateStory({
                    variables: {
                        data: {
                            storyUid: uid,
                            status: status,
                        },
                    },
                });
                if (!updateStoryResult?.errors?.length) {
                    toast.success(STORIES_LIST_CONTENT.storyStatusChangedSuccessfully, {
                        hideProgressBar: true,
                        containerId: 'success',
                    });
                    void refetch();
                }
            } catch (error) {
                console.error('updateStoryAction error:', error);
            }
        },
        [updateStory, refetch]
    );

    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);

    const toggleIsOpenConfirmDialog = useCallback(() => {
        setIsOpenConfirmDialog(!isOpenConfirmDialog);
    }, [isOpenConfirmDialog]);

    const editStory = useCallback(
        (uid) => {
            history.push(`/story/editor/${uid}`);
        },
        [history]
    );

    const [confirmText, setConfirmText] = useState('');
    const [firstSentenceText, setFirstSentenceText] = useState('');
    const [storyUidForEditing, setStoryUidForEditing] = useState('');
    const [storyStatus, setStoryStatus] = useState<StoryStatus>();

    const storyAction = useCallback(
        (uid, storyAction: StoryAction) => {
            if (storyAction === StoryAction.EDIT) {
                editStory(uid);
                return;
            }
            setStoryUidForEditing(uid);
            switch (storyAction) {
                case StoryAction.ARCHIVE: {
                    setStoryStatus(StoryStatus.Archive);
                    break;
                }
                case StoryAction.UNZIP: {
                    setStoryStatus(StoryStatus.Active);
                    break;
                }
                case StoryAction.DELETE: {
                    setStoryStatus(StoryStatus.Deleted);
                    break;
                }
                default: {
                    break;
                }
            }
            setIsOpenConfirmDialog(true);
            const confirmTextProp = `${storyAction}ConfirmText` as keyof typeof STORIES_LIST_CONTENT;
            setConfirmText(STORIES_LIST_CONTENT[confirmTextProp] || '');
            const firstSentenceTextProp = `${storyAction}FirstSentenseText` as keyof typeof STORIES_LIST_CONTENT;
            setFirstSentenceText(STORIES_LIST_CONTENT[firstSentenceTextProp] || '');
        },
        [editStory]
    );

    const closeConfirmModal = useCallback(() => {
        if (storyUidForEditing && storyStatus) {
            updateStoryAction(storyUidForEditing, storyStatus);
        }
        toggleIsOpenConfirmDialog();
    }, [storyUidForEditing, storyStatus, updateStoryAction, toggleIsOpenConfirmDialog]);

    console.log('Rendering StoriesListContent with data:', adaptedData?.edges);
    console.log('isEmptyList:', isEmptyList);
    console.log('hasMore:', !!data?.stories?.pageInfo.hasNextPage);
    console.log('loading:', loading);
    console.log('cursor:', data?.stories?.pageInfo.endCursor);

    return (
        <>
            {!isEmptyList ? (
                <InfiniteScroll
                    dataLength={adaptedData?.edges.length || 0}
                    next={fetchMoreData}
                    hasMore={!!data?.stories?.pageInfo.hasNextPage && !loading}
                    loader={<LinearProgress color="primary" />}
                    height="calc(100vh - 430px)"
                    scrollThreshold={0.95}
                    style={{ overflow: 'auto', marginTop: '24px' }}
                >
                    <Box>
                        {adaptedData?.edges?.map((edge, index) => {
                            console.log(`Rendering edge ${index}:`, edge);
                            if (!edge?.node) return null;
                            return (
                                <StoriesItem
                                    story={edge.node as Story}
                                    key={edge.node.id || edge.node.uid || index}
                                    storyAction={storyAction}
                                />
                            );
                        })}
                    </Box>
                </InfiniteScroll>
            ) : (
                <Box marginTop="15px">
                    <Typography variant="h2">{STORIES_LIST_CONTENT.storiesNotFound}</Typography>
                </Box>
            )}
            <ConfirmationModal
                id="confirmation-story-action"
                confirmText={confirmText}
                isOpenConfirmDialog={isOpenConfirmDialog}
                closeConfirmModal={closeConfirmModal}
                okLabel={STORIES_LIST_CONTENT.yes}
                toggleIsOpenConfirmDialog={toggleIsOpenConfirmDialog}
                firstSentenceText={firstSentenceText}
            />
        </>
    );
};

export default observer(StoriesListContent);